import React, { useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormContext } from 'react-hook-form';
import { useBilledEntities } from 'src/use-cases/settings/useBilledEntities';
import { useCreditTransactionsMetadata } from 'src/use-cases/settings/useCreditTransactions';
import { TransactionDateMenu } from './TransactionDateMenu';
import { BilledEntitiesMenu } from './BilledEntitiesMenu';
import { formatTransactionDateDisplayText } from './creditTransactionsUtils';
import { FieldsetDropdown } from '../../common/FieldsetDropdown';

// Use same styling as FilterHeader
const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '250px',
    [theme.breakpoints.down(1520)]: {
      width: '200px',
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
  },
  '& .MuiPaper-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    border: '1px solid #DFDFDF',
  },
  [theme.breakpoints.down(1520)]: {
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
  },
}));

export const CreditTransactionsHeader: React.FC = () => {
  // Get form context from parent FormContainer
  const { watch } = useFormContext();
  const formValues = watch();

  // State for transaction date menu visibility
  const [isTransactionDateMenuVisible, setIsTransactionDateMenuVisible] = useState(false);
  const [isBilledEntitiesMenuVisible, setIsBilledEntitiesMenuVisible] = useState(false);

  // Fetch billed entities for the dropdown
  const { data: billedEntities, isLoading: isLoadingEntities } = useBilledEntities();

  // Get current selections
  const selectedBilledEntityId = formValues.billedEntityId ? Number(formValues.billedEntityId) : 0;
  const selectedTransactionDates = formValues.transactionDate || [];

  // Fetch credit transaction metadata to get available transaction dates
  // This is filtered by the selected billed entity ID to avoid conflicts with the table's metadata call
  const metadataFilters = useMemo(() => {
    const filters: any = {};
    if (selectedBilledEntityId) {
      filters.creditBilledEntityId = selectedBilledEntityId;
    }
    return filters;
  }, [selectedBilledEntityId]);

  const { data: metadataData, isLoading: isLoadingMetadata } = useCreditTransactionsMetadata(metadataFilters);

  // Get available transaction dates from metadata
  const availableTransactionDates = metadataData?.data?.transactionDates || [];

  // Format display text for transaction date button - this will update when transactionDate changes
  const transactionDateDisplayText = useMemo(
    () => formatTransactionDateDisplayText(selectedTransactionDates, 'Transaction Date'),
    [selectedTransactionDates, selectedBilledEntityId]
  );

  const billedEntityDisplayText = useMemo(() => {
    if (!selectedBilledEntityId) {
      return 'Billed Entity';
    }

    // Helper function to recursively search through entities and sub-entities
    const findEntityById = (entities: any[], targetId: number): any => {
      for (const entity of entities) {
        if (entity.id === targetId) {
          return entity;
        }
        // Check sub-entities recursively
        if (entity.subEntities && entity.subEntities.length > 0) {
          const found = findEntityById(entity.subEntities, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const foundEntity = billedEntities ? findEntityById(billedEntities, selectedBilledEntityId) : null;
    return foundEntity?.code || 'Billed Entity';
  }, [selectedBilledEntityId, billedEntities]);

  if (isLoadingEntities) {
    return (
      <div className='flex items-center justify-center h-full'>
        <CircularProgress size={32} />
      </div>
    );
  }

  const handleTransactionDateToggle = (event?: React.MouseEvent) => {
    // Prevent the event from bubbling up
    if (event) {
      event.stopPropagation();
    }
    setIsTransactionDateMenuVisible(!isTransactionDateMenuVisible);
  };

  const handleTransactionDateClose = () => {
    setIsTransactionDateMenuVisible(false);
  };

  const handleTransactionDateApply = (selectedDates: string[]) => {
    // console.log('Selected transaction dates:', selectedDates);
  };

  const handleBilledEntitiesToggle = (event?: React.MouseEvent) => {
    // Prevent the event from bubbling up
    if (event) {
      event.stopPropagation();
    }
    setIsBilledEntitiesMenuVisible(!isBilledEntitiesMenuVisible);
  };
  const handleBilledEntitiesClose = () => {
    setIsBilledEntitiesMenuVisible(false);
  };
  const handleBilledEntitiesApply = (selectedDates: string[]) => {
    console.log('Selected billed entities:', selectedDates);
  };

  // Check if transaction date display text is empty (no selection)
  const isTransactionDateEmpty = transactionDateDisplayText === 'Transaction Date';

  // Check if billed entity display text is empty (no selection)
  const isBilledEntityEmpty = billedEntityDisplayText === 'Billed Entity';

  return (
    <div className='flex items-center gap-12 xl:gap-16'>
      {/* Billed Entity Dropdown */}
      <FieldsetDropdown
        legend='Select Credit Entity'
        displayText={billedEntityDisplayText}
        isMenuVisible={isBilledEntitiesMenuVisible}
        onToggle={handleBilledEntitiesToggle}
        showLegend={!isBilledEntityEmpty}
      >
        <BilledEntitiesMenu
          billedEntities={billedEntities || []}
          isVisible={isBilledEntitiesMenuVisible}
          onClose={handleBilledEntitiesClose}
          onToggle={handleBilledEntitiesToggle}
          onApply={handleBilledEntitiesApply}
        />
      </FieldsetDropdown>

      {/* Transaction Date Button with Custom Menu */}
      <FieldsetDropdown
        legend='Showing Transaction for'
        displayText={transactionDateDisplayText}
        isMenuVisible={isTransactionDateMenuVisible}
        onToggle={handleTransactionDateToggle}
        disabled={!selectedBilledEntityId}
        showLegend={!isTransactionDateEmpty}
      >
        <TransactionDateMenu
          isVisible={isTransactionDateMenuVisible}
          onClose={handleTransactionDateClose}
          onToggle={handleTransactionDateToggle}
          onApply={handleTransactionDateApply}
          availableTransactionDates={availableTransactionDates}
          isLoading={isLoadingMetadata}
        />
      </FieldsetDropdown>
    </div>
  );
};
